/**
 * AdminSystemHealth — Phase 14 Wave 1, Module G
 * KPI strip + recent errors feed. Backed by `/api/admin/health`.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, AlertTriangle, Cpu, Database, Loader2, RefreshCw } from "lucide-react";

function KpiCard({ label, value, sub, tone = "neutral", icon: Icon, testid }) {
  const toneMap = {
    good: "border-emerald-200 bg-emerald-50",
    bad: "border-red-200 bg-red-50",
    neutral: "border-slate-200 bg-white",
  };
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${toneMap[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        {label}
      </div>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
      {sub ? <p className="mt-1 text-xs text-slate-500">{sub}</p> : null}
    </div>
  );
}

export default function AdminSystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/health");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 fade-in" data-testid="admin-health">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Operations</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">System Health</h1>
          <p className="mt-1 text-sm text-slate-500">Live status of MongoDB, AI service, FX cache, and recent errors.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="health-refresh">
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : !data ? (
        <p className="text-sm text-red-600">Could not load health endpoint.</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="MongoDB · Users"
              value={data.mongo?.users ?? 0}
              sub={`${data.mongo?.properties ?? 0} properties · ${data.mongo?.deal_analyses ?? 0} deals`}
              icon={Database}
              tone="good"
              testid="health-mongo"
            />
            <KpiCard
              label="AI service"
              value={data.ai_service?.enabled ? "Online" : "Offline"}
              sub={data.ai_service?.analyst_model || "n/a"}
              tone={data.ai_service?.enabled ? "good" : "bad"}
              icon={Cpu}
              testid="health-ai"
            />
            <KpiCard
              label="AI calls (24h)"
              value={data.ai_calls_24h?.count ?? 0}
              sub={`${data.ai_calls_24h?.errors ?? 0} errors · ${data.ai_calls_24h?.avg_latency_ms ?? 0} ms avg`}
              tone={(data.ai_calls_24h?.errors || 0) > 0 ? "bad" : "good"}
              icon={Activity}
              testid="health-ai-24h"
            />
            <KpiCard
              label="FX cache"
              value={`${data.fx_cache?.currencies ?? 0} currencies`}
              sub={data.fx_cache?.source ? `via ${data.fx_cache.source}` : "no fresh data"}
              icon={Activity}
              testid="health-fx"
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">Supervisor processes</h2>
            <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
              {Object.keys(data.supervisor || {}).length ? Object.entries(data.supervisor).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2">
                  <span className="font-mono">{k}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v === "RUNNING" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{v}</span>
                </div>
              )) : <p className="text-slate-400">Supervisor info unavailable in this environment.</p>}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="health-errors">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Recent backend errors
            </h2>
            {data.recent_errors?.length ? (
              <ul className="mt-3 max-h-96 space-y-1 overflow-y-auto font-mono text-[11px] leading-relaxed text-red-800">
                {data.recent_errors.map((line, i) => (
                  // Phase 46 Batch D — stable key. Log lines may
                  // occasionally repeat, so suffix the index so React
                  // never deduplicates a legitimate repeat. This is
                  // still strictly more stable than `key={i}` because
                  // a reordered window of errors keeps element
                  // identity attached to the line content.
                  <li key={`${line}-${i}`} className="rounded bg-red-50 px-2 py-1">{line}</li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-xs text-slate-400">No recent errors — all clear.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
